import React, { useState, useEffect } from 'react';
import { Shield, CheckCircle, AlertTriangle, Smartphone } from 'lucide-react';
import { ANTI_BOT_CONFIG, calculateVerificationScore, type VerificationResult } from '../config/anti-bot';

interface WhatsAppVerificationProps {
  phoneNumber: string;
  message: string;
  children: React.ReactNode;
  className?: string;
  onVerified?: () => void;
}

export function WhatsAppVerification({ 
  phoneNumber, 
  message, 
  children, 
  className = "",
  onVerified 
}: WhatsAppVerificationProps) {
  const [isVerified, setIsVerified] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationStep, setVerificationStep] = useState<'idle' | 'verifying' | 'success' | 'failed'>('idle');
  const [userInteraction, setUserInteraction] = useState(false);

  // Detectar si es un bot basado en comportamiento
  useEffect(() => {
    let interactionTimeout: ReturnType<typeof setTimeout>;
    
    // Detectar interacciones humanas
    const handleHumanInteraction = () => {
      setUserInteraction(true);
      interactionTimeout = setTimeout(() => setUserInteraction(false), ANTI_BOT_CONFIG.USER_INTERACTION_TIMEOUT);
    };

    // Eventos que indican interacción humana
    const events = ANTI_BOT_CONFIG.HUMAN_INTERACTION_EVENTS;
    
    events.forEach(event => {
      document.addEventListener(event, handleHumanInteraction, { passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleHumanInteraction);
      });
      if (interactionTimeout) clearTimeout(interactionTimeout);
    };
  }, []);

  // Verificación anti-bot
  const handleVerification = async () => {
    if (!userInteraction) {
      setVerificationStep('failed');
      return;
    }

    setVerificationStep('verifying');
    
    // Simular verificación (en producción, aquí iría la lógica real)
    await new Promise(resolve => setTimeout(resolve, ANTI_BOT_CONFIG.DELAYS.VERIFICATION_PROCESS));
    
    // Verificaciones adicionales
    const verificationResult = await performHumanVerification();
    
    if (verificationResult.isHuman) {
      setVerificationStep('success');
      setIsVerified(true);
      onVerified?.();
      
      // Redirigir a WhatsApp después de un breve delay
      setTimeout(() => {
        const encodedMessage = encodeURIComponent(message);
        const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
        window.open(whatsappUrl, '_blank');
      }, ANTI_BOT_CONFIG.DELAYS.WHATSAPP_REDIRECT);
    } else {
      setVerificationStep('failed');
    }
  };

  // Verificación de humanidad
  const performHumanVerification = async (): Promise<VerificationResult> => {
    // Verificaciones básicas
    const checks = [
      // Verificar que el usuario haya interactuado con la página
      { name: 'userInteraction', passed: userInteraction },
      
      // Verificar que no sea un headless browser
      { name: 'headlessBrowser', passed: !navigator.webdriver },
      
      // Verificar plugins (bots suelen no tener plugins)
      { name: 'plugins', passed: navigator.plugins.length > ANTI_BOT_CONFIG.SUSPICIOUS_BROWSER_INDICATORS.MIN_PLUGINS_COUNT },
      
      // Verificar que tenga un user agent realista
      { name: 'userAgent', passed: navigator.userAgent.length > ANTI_BOT_CONFIG.SUSPICIOUS_BROWSER_INDICATORS.MIN_USER_AGENT_LENGTH },
      
      // Verificar que tenga cookies habilitadas
      { name: 'cookies', passed: navigator.cookieEnabled },
      
      // Verificar que tenga JavaScript habilitado
      { name: 'javascript', passed: typeof window !== 'undefined' },
      
      // Verificar que tenga una resolución de pantalla realista
      { name: 'screenResolution', passed: screen.width > ANTI_BOT_CONFIG.SUSPICIOUS_BROWSER_INDICATORS.MIN_SCREEN_WIDTH && screen.height > ANTI_BOT_CONFIG.SUSPICIOUS_BROWSER_INDICATORS.MIN_SCREEN_HEIGHT },
      
      // Verificar que tenga un timezone
      { name: 'timezone', passed: Intl.DateTimeFormat().resolvedOptions().timeZone !== '' },
    ];

    const passedChecks = checks.filter(check => check.passed).length;
    const totalChecks = checks.length;
    const failedChecks = checks.filter(check => !check.passed).map(check => check.name);
    const score = calculateVerificationScore(passedChecks, totalChecks);
    
    const isHuman = passedChecks >= ANTI_BOT_CONFIG.MIN_VERIFICATION_CHECKS;
    
    return {
      isHuman,
      passedChecks,
      totalChecks,
      failedChecks,
      score
    };
  };

  // Si ya está verificado, mostrar el enlace directo
  if (isVerified) {
    return (
      <div className={`${className} cursor-pointer`}>
        {children}
      </div>
    );
  }

  // Mostrar verificación anti-bot
  if (showVerification) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-neuro-blue/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-neuro-blue" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Verificación de Seguridad
            </h3>
            <p className="text-gray-600 text-sm">
              {ANTI_BOT_CONFIG.MESSAGES.CONFIRM_HUMAN}
            </p>
          </div>

          {verificationStep === 'idle' && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <Smartphone className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">
                  {ANTI_BOT_CONFIG.MESSAGES.WHATSAPP_REDIRECT}
                </p>
              </div>
              
              <button
                onClick={handleVerification}
                className="w-full bg-neuro-green text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                Confirmar que soy humano
              </button>
              
              <button
                onClick={() => setShowVerification(false)}
                className="w-full text-gray-500 py-2 px-4 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancelar
              </button>
            </div>
          )}

          {verificationStep === 'verifying' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-neuro-blue mx-auto mb-4"></div>
              <p className="text-gray-600">{ANTI_BOT_CONFIG.MESSAGES.VERIFYING}</p>
            </div>
          )}

          {verificationStep === 'success' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h4 className="text-lg font-semibold text-green-800 mb-2">
                ¡Verificación Exitosa!
              </h4>
              <p className="text-green-600 text-sm">
                {ANTI_BOT_CONFIG.MESSAGES.VERIFICATION_SUCCESS}
              </p>
            </div>
          )}

          {verificationStep === 'failed' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <h4 className="text-lg font-semibold text-red-800 mb-2">
                Verificación Fallida
              </h4>
              <p className="text-red-600 text-sm mb-4">
                {ANTI_BOT_CONFIG.MESSAGES.VERIFICATION_FAILED}
              </p>
              
              <button
                onClick={() => {
                  setVerificationStep('idle');
                  setUserInteraction(false);
                }}
                className="bg-neuro-blue text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors"
              >
                Intentar de nuevo
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Botón que activa la verificación
  return (
    <div 
      className={`${className} cursor-pointer`}
      onClick={() => setShowVerification(true)}
    >
      {children}
    </div>
  );
}
